import React from 'react';
import { Button } from '../ui/button';
import {
  UserPlus, Heart, MessageCircle, Calendar, Gift, HelpCircle, BarChart3, FileText, ThumbsUp,
  CheckCircle2, Users, Clock, MessageSquare, X as XIcon, Banknote, CalendarPlus,
} from 'lucide-react';

export const ICON_MAP = {
  follow: { icon: UserPlus, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/40' },
  like: { icon: Heart, color: 'text-pink-500', bg: 'bg-pink-100 dark:bg-pink-900/40' },
  comment: { icon: MessageCircle, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/40' },
  message: { icon: MessageSquare, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/40' },
  new_message: { icon: MessageSquare, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/40' },
  booking: { icon: Calendar, color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/40' },
  tip: { icon: Gift, color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/40' },
  community_question: { icon: HelpCircle, color: 'text-indigo-500', bg: 'bg-indigo-100 dark:bg-indigo-900/40' },
  community_answer: { icon: MessageCircle, color: 'text-cyan-500', bg: 'bg-cyan-100 dark:bg-cyan-900/40' },
  community_poll: { icon: BarChart3, color: 'text-violet-500', bg: 'bg-violet-100 dark:bg-violet-900/40' },
  community_post: { icon: FileText, color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-900/40' },
  community_vote: { icon: ThumbsUp, color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/40' },
  community_resolved: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/40' },
  community_join: { icon: Users, color: 'text-teal-500', bg: 'bg-teal-100 dark:bg-teal-900/40' },
  community_join_request: { icon: UserPlus, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/40' },
  community_join_accepted: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/40' },
  community_join_rejected: { icon: XIcon, color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-700' },
  community_welcome: { icon: Users, color: 'text-teal-500', bg: 'bg-teal-100 dark:bg-teal-900/40' },
  expert_interest: { icon: Heart, color: 'text-rose-500', bg: 'bg-rose-100 dark:bg-rose-900/40' },
  community_event: { icon: Calendar, color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/40' },
  product_like: { icon: ThumbsUp, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/40' },
  product_comment: { icon: MessageCircle, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/40' },
  event_participation: { icon: Users, color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/40' },
  session_invitation_received: { icon: Calendar, color: 'text-violet-600', bg: 'bg-violet-100 dark:bg-violet-900/40' },
  booking_pending_payment: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/40' },
  payment_received: { icon: Banknote, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/40' },
  session_request_received: { icon: CalendarPlus, color: 'text-violet-600', bg: 'bg-violet-100 dark:bg-violet-900/40' },
  session_request_accepted: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/40' },
  session_request_declined: { icon: XIcon, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/40' },
  session_request_counter: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/40' },
  session_started: { icon: Calendar, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/40' },
};

export const NotificationIcon = ({ type }) => {
  const config = ICON_MAP[type] || ICON_MAP.message;
  const Icon = config.icon;
  return (
    <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`h-5 w-5 ${config.color}`} />
    </div>
  );
};

export const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours} h`;
  if (diffDays < 7) return `Il y a ${diffDays} j`;
  return date.toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' });
};

// Cible de navigation d'une notification — source unique (dropdown + page).
export const getNotificationTarget = (notification) => {
  const data = notification.data || {};
  switch (notification.type) {
    case 'follow': return `/app/profile/${data.user_id}`;
    case 'comment':
      if (data.post_id) return `/app/feed?post=${data.post_id}&comments=1`;
      if (data.product_id) return `/app/shop/${data.owner_id || data.user_id}?product=${data.product_id}`;
      return '/app/feed';
    case 'like': return data.post_id ? `/app/feed?post=${data.post_id}` : '/app/feed';
    case 'message':
    case 'new_message': return `/app/messages/${data.user_id}`;
    case 'booking': return `/app/sessions?filter=pending${data.booking_id ? `&highlight=${data.booking_id}` : ''}`;
    case 'tip': return '/app/dashboard?tab=payments';
    case 'community_question':
    case 'community_answer':
    case 'community_vote':
    case 'community_resolved': return data.community_id ? `/app/community/${data.community_id}?tab=questions` : null;
    case 'community_poll': return data.community_id ? `/app/community/${data.community_id}?tab=polls` : null;
    case 'community_event':
    case 'community_post':
    case 'community_join':
    case 'community_join_accepted':
    case 'community_welcome':
    case 'community_join_request': return data.community_id ? `/app/community/${data.community_id}` : null;
    case 'community_join_rejected': return '/app/community';
    case 'expert_interest': return '/app/sessions?filter=interested';
    case 'product_like':
    case 'product_comment': return data.owner_id ? `/app/shop/${data.owner_id}${data.product_id ? `?product=${data.product_id}` : ''}` : null;
    case 'event_participation':
    case 'booking_pending_payment':
    case 'payment_received': {
      const hl = data.booking_id || data.event_id;
      return `/app/sessions${hl ? `?highlight=${hl}` : ''}`;
    }
    case 'session_started':
      if (data.url) return { external: data.url };
      return `/app/sessions${data.event_id ? `?highlight=${data.event_id}` : ''}`;
    case 'session_request_received':
    case 'session_request_counter':
    case 'session_request_declined':
    case 'session_invitation_received': return '/app/sessions?filter=pending';
    case 'session_request_accepted': return '/app/sessions';
    default: return null;
  }
};

/* Ligne de notification — partagée par le dropdown (cloche) et la page /app/notifications.
   Brief 27 oct C.2 : la demande d'adhésion se traite inline (Accepter / Refuser). */
export const NotificationItem = ({ notification, accent = 'purple', onOpen, joinDecision, onRespondJoin, compact = true }) => {
  const unread = !notification.is_read;
  return (
    <div
      onClick={() => onOpen(notification)}
      className={`flex items-start gap-3 ${compact ? 'p-4' : 'p-4 sm:p-5'} cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/60 ${
        unread ? `bg-${accent}-50/50 dark:bg-${accent}-900/20` : ''
      }`}
      data-testid={`notification-${notification.notification_id}`}
    >
      <NotificationIcon type={notification.type} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${unread ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
          {notification.message}
        </p>
        <p className="text-xs text-gray-400 mt-1 flex items-center">
          <Clock className="h-3 w-3 mr-1" />
          {formatTime(notification.created_at)}
        </p>
        {notification.type === 'community_join_request' && notification.data?.request_id && (() => {
          if (joinDecision === 'accepted' || joinDecision === 'rejected') {
            return (
              <p className={`text-xs font-medium mt-2 ${joinDecision === 'accepted' ? 'text-emerald-600' : 'text-gray-500'}`} data-testid={`join-request-decided-${notification.notification_id}`}>
                {joinDecision === 'accepted' ? '✓ Demande acceptée — le membre a accès à la communauté' : 'Demande refusée'}
              </p>
            );
          }
          return (
            <div className="flex items-center gap-2 mt-2" data-testid={`join-request-actions-${notification.notification_id}`}>
              <Button size="sm" className="h-7 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" disabled={joinDecision === 'loading'} onClick={(e) => onRespondJoin(e, notification, true)} data-testid={`join-request-accept-${notification.notification_id}`}>
                Accepter
              </Button>
              <Button size="sm" variant="outline" className="h-7 px-3 text-xs" disabled={joinDecision === 'loading'} onClick={(e) => onRespondJoin(e, notification, false)} data-testid={`join-request-reject-${notification.notification_id}`}>
                Refuser
              </Button>
              {joinDecision === 'error' && <span className="text-xs text-red-500">Réessaie</span>}
            </div>
          );
        })()}
      </div>
      {unread && <div className={`w-2 h-2 bg-${accent}-500 rounded-full flex-shrink-0 mt-2`} data-testid={`notification-unread-dot-${notification.notification_id}`}></div>}
    </div>
  );
};
